import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '@/services/api';
import { tokenManager } from '@/services/tokenManager';
import type { User, LoginRequest } from '@/types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginRequest, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  hasRole: (role: string) => boolean;
  canAccess: (resource: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in on app start
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      if (apiService.isAuthenticated()) {
        const currentUser = apiService.getCurrentUser();
        if (currentUser) {
          // Set user from stored data first
          setUser(currentUser);
        }
      }
    } catch (error) {
      // Token is invalid, clear storage but don't call logout to avoid infinite loop
      console.warn('Authentication check failed:', error);
      // Clear tokens directly without calling logout
      tokenManager.clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials: LoginRequest, rememberMe: boolean = false) => {
    try {
      const response = await apiService.login(credentials, rememberMe);
      setUser(response.user);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    // Prevent multiple simultaneous logout calls
    if (loading) {
      return;
    }

    try {
      setLoading(true);

      // Only call logout API if we have a valid token
      if (apiService.isAuthenticated()) {
        try {
          await apiService.logout();
        } catch (error) {
          // If logout API fails (e.g., token expired), just clear local state
          console.warn('Logout API failed, clearing local state:', error);
        }
      }
    } catch (error) {
      // Even if logout fails on server, clear local state
      console.error('Logout error:', error);
    } finally {
      // Always clear local state
      tokenManager.clearTokens();
      setUser(null);
      setLoading(false);
    }
  };

  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  const canAccess = (resource: string): boolean => {
    if (!user) return false;

    const permissions = {
      admin: ['all'],
      dispatch: ['orders', 'inventory', 'routes', 'reports'],
      warehouse: ['orders', 'inventory', 'picking', 'threepl', 'hub', 'routes'],
      office: ['orders', 'quotes', 'customers', 'threepl', 'hub'],
      driver: ['driver'],
    };

    const userPermissions = permissions[user.role as keyof typeof permissions] || [];
    return userPermissions.includes('all') || userPermissions.includes(resource);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    hasRole,
    canAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}